import type { CertificateBucketKey, CertificateBuckets } from '../../../types/models/Certificate';

export type { CertificateBucketKey, CertificateBuckets };

/** Display order used in both the KPI grid and per-platform cards. */
export const CERTIFICATE_BUCKET_ORDER: CertificateBucketKey[] = [
  'request',
  'valid',
  'expiring_soon',
  'expiring_later',
  'expired',
  'revoked',
];

export const CERTIFICATE_BUCKET_LABELS: Record<CertificateBucketKey, string> = {
  request: 'Pending requests',
  valid: 'Valid',
  expiring_soon: 'Expiring soon',
  expiring_later: 'Expiring later',
  expired: 'Expired',
  revoked: 'Revoked',
};

export type CertificateBucketColor = 'blue' | 'green' | 'orange' | 'yellow' | 'red' | 'grey';

/** PatternFly Label colour per bucket. */
export const CERTIFICATE_BUCKET_LABEL_COLORS: Record<CertificateBucketKey, CertificateBucketColor> = {
  request: 'blue',
  valid: 'green',
  expiring_soon: 'orange',
  expiring_later: 'yellow',
  expired: 'red',
  revoked: 'grey',
};

/**
 * CSS hex values per bucket, intended for use as Victory ChartPie `colorScale`
 * entries. Kept visually aligned with the Label colours above; tweak both
 * tables together if you change one.
 */
export const CERTIFICATE_BUCKET_CHART_COLORS: Record<CertificateBucketKey, string> = {
  request: '#0066cc',
  valid: '#3e8635',
  expiring_soon: '#c46100',
  expiring_later: '#f4c145',
  expired: '#b1380b',
  revoked: '#6a6e73',
};

interface ClassifiableCertificate {
  status: 'request' | 'ok' | 'expired' | 'revoked';
  not_after: string | null;
  revoked_at: string | null;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Mirror of the backend bucket precedence documented in
 * api-types/certificate-platforms.md (Certificate Stats).
 *
 * Precedence (first match wins):
 *   1. status === 'request'                          → request
 *   2. status === 'revoked' OR revoked_at !== null   → revoked
 *   3. not_after < now                               → expired
 *   4. not_after <= now + 30d                        → expiring_soon
 *   5. not_after <= now + 90d                        → expiring_later
 *   6. otherwise                                     → valid
 *
 * Time wins over status === 'ok': a row whose stored status went stale is
 * bucketed by `not_after`, matching the backend.
 */
export function classifyCertificateBucket(
  cert: ClassifiableCertificate,
  now: number = Date.now(),
): CertificateBucketKey {
  if (cert.status === 'request') {
    return 'request';
  }
  if (cert.status === 'revoked' || cert.revoked_at !== null) {
    return 'revoked';
  }
  if (cert.not_after !== null) {
    const expiresAt = Date.parse(cert.not_after);
    if (!Number.isNaN(expiresAt)) {
      if (expiresAt < now) {
        return 'expired';
      }
      if (expiresAt <= now + THIRTY_DAYS_MS) {
        return 'expiring_soon';
      }
      if (expiresAt <= now + NINETY_DAYS_MS) {
        return 'expiring_later';
      }
    }
  }
  return 'valid';
}

/** Type guard used by URL-param hydration. */
export function isCertificateBucketKey(value: unknown): value is CertificateBucketKey {
  return typeof value === 'string' && (CERTIFICATE_BUCKET_ORDER as string[]).includes(value);
}
